/**
 * VesselMind FAQ Service
 * 
 * Handles all FAQ-related API calls
 * Isolated from main app - uses /api/vesselmind endpoints
 */

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Check if a query has a cached FAQ answer
 * @param {string} query - User's question
 * @param {string} sessionId - Session identifier
 * @returns {Promise<Object>} Response with cached answer or indication to use API
 */
export const checkFAQCache = async (query, sessionId, conversationContext = null) => {
  try {
    const payload = { query, sessionId };
    if (conversationContext) {
      payload.conversationContext = conversationContext;
    }

    const response = await fetch(`${API_BASE}/api/vesselmind/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error('Failed to check FAQ cache');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking FAQ cache:', error);
    return {
      success: false,
      responseType: 'error',
      error: error.message
    };
  }
};

/**
 * Log an API-generated response
 * @param {Object} responseData - Response details
 * @returns {Promise<Object>}
 */
export const logAPIResponse = async (responseData) => {
  try {
    const response = await fetch(`${API_BASE}/api/vesselmind/log-response`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(responseData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to log response');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error logging response:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get authentication token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Get all FAQs (Admin only)
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>}
 */
export const getAllFAQs = async (filters = {}) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const queryParams = new URLSearchParams(filters).toString();
    const url = `${API_BASE}/api/vesselmind/admin/faqs${queryParams ? '?' + queryParams : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch FAQs');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    throw error;
  }
};

/**
 * Get pending queries for review (Admin only)
 * @returns {Promise<Array>}
 */
export const getPendingQueries = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/pending`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch pending queries');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching pending queries:', error);
    throw error;
  }
};

/**
 * Create a new FAQ (Admin only)
 * @param {Object} faqData - FAQ data
 * @returns {Promise<Object>}
 */
export const createFAQ = async (faqData) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/faq`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(faqData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to create FAQ');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating FAQ:', error);
    throw error;
  }
};

/**
 * Update an existing FAQ (Admin only)
 * @param {string} id - FAQ ID
 * @param {Object} updates - Updated fields
 * @returns {Promise<Object>}
 */
export const updateFAQ = async (id, updates) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/faq/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      throw new Error('Failed to update FAQ');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating FAQ:', error);
    throw error;
  }
};

/**
 * Approve a draft FAQ (Admin only)
 * @param {string} id - FAQ ID
 * @returns {Promise<Object>}
 */
export const approveFAQ = async (id) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/faq/${id}/approve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to approve FAQ');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error approving FAQ:', error);
    throw error;
  }
};

/**
 * Delete (archive) a FAQ (Admin only)
 * @param {string} id - FAQ ID
 * @returns {Promise<Object>}
 */
export const deleteFAQ = async (id) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/faq/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete FAQ');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting FAQ:', error);
    throw error;
  }
};

/**
 * Approve a query and convert to FAQ (Admin only)
 * @param {string} queryId - Query log ID
 * @param {Object} faqData - FAQ data
 * @returns {Promise<Object>}
 */
export const approveQuery = async (queryId, faqData) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/approve-query/${queryId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(faqData)
    });
    
    if (!response.ok) {
      throw new Error('Failed to approve query');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error approving query:', error);
    throw error;
  }
};

/**
 * Dismiss a query without creating FAQ (Admin only)
 * @param {string} queryId - Query log ID
 * @returns {Promise<Object>}
 */
export const dismissQuery = async (queryId) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/dismiss-query/${queryId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to dismiss query');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error dismissing query:', error);
    throw error;
  }
};

/**
 * Get analytics data (Admin only)
 * @param {number} days - Number of days to analyze
 * @returns {Promise<Object>}
 */
export const getAnalytics = async (days = 30) => {
  try {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    const response = await fetch(`${API_BASE}/api/vesselmind/admin/analytics?days=${days}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch analytics');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching analytics:', error);
    throw error;
  }
};

